import React, { ReactNode } from 'react'
import { twMerge } from 'tailwind-merge'

import { tva } from '../../tailwind/merge'
import { Box } from '../box'
import { HStack } from '../hstack'
import { Pressable } from '../pressable'
import { ChevronDownGlyph } from '../primitive-glyphs/glyphs'

export type CollapsibleProps = {
    label: ReactNode
    isCollapsed: boolean
    onToggle(): void
    children?: ReactNode
    chevronPosition?: 'leading' | 'trailing'
    chevronColor?: string
    labelJustify?: 'start' | 'end'
    borderedTop?: boolean
    borderedBottom?: boolean
    className?: string
    testID?: string
}

const labelWrapperStyle = tva({
    base: 'flex-1',
    variants: {
        labelJustify: {
            start: '',
            end: 'justify-end',
        },
    },
})

/**
 * Universal controlled collapsible section (replaces the legacy `aos-ui`
 * `CollapsibleContainer` at event usage sites): a full-width header row —
 * a `label` slot plus a chevron that points down when open and rotates up when
 * collapsed — over a body that renders only while expanded. Boolean-controlled
 * (`isCollapsed`/`onToggle`) to match the legacy API 1:1.
 */
export const Collapsible: React.FC<CollapsibleProps> = ({
    label,
    isCollapsed,
    onToggle,
    children,
    chevronPosition = 'trailing',
    chevronColor,
    labelJustify = 'start',
    borderedTop = false,
    borderedBottom = false,
    className,
    testID,
}) => {
    const chevron = (
        // rotate-0, not '': on native the transform has to be present from the first
        // render. Introducing one later makes NativeWind upgrade the component to
        // animated, and the warning it logs stringifies props by walking getters —
        // which trips react-navigation's throwing context guard and surfaces as a
        // bogus "Couldn't find a navigation context" render error.
        <Box className={isCollapsed ? 'rotate-180' : 'rotate-0'}>
            <ChevronDownGlyph size={20} color={chevronColor} />
        </Box>
    )
    return (
        <>
            <Pressable
                onPress={onToggle}
                accessibilityRole='button'
                accessibilityState={{ expanded: !isCollapsed }}
                testID={testID}
                className={twMerge(
                    'flex-row items-center gap-2 py-4',
                    borderedTop && 'border-t border-solid border-border-subtle',
                    borderedBottom && 'border-b border-solid border-border-subtle',
                    className,
                )}
            >
                {chevronPosition === 'leading' && chevron}
                <HStack className={labelWrapperStyle({ labelJustify })}>{label}</HStack>
                {chevronPosition === 'trailing' && chevron}
            </Pressable>
            {!isCollapsed && children}
        </>
    )
}

Collapsible.displayName = 'Collapsible'
